import { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { curriculumList, benefits } from '@/mocks/academyData';
import { instructors } from '@/mocks/aboutData';
import { supabase } from '@/lib/supabase';

function useAnim(threshold = 0.1) {
  const ref = useRef<HTMLElement | null>(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setV(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, v };
}

export default function AcademyPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const sBanner = useAnim(0.1);
  const sCurriculum = useAnim(0.08);
  const sInstructor = useAnim(0.08);
  const sBenefits = useAnim(0.08);
  const sFee = useAnim(0.1);
  const sConsult = useAnim(0.1);

  useEffect(() => {
    if (window.location.hash === '#consult') {
      setTimeout(() => {
        const el = document.getElementById('consult');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const name = formData.get('name') as string;
    const phone = formData.get('phone') as string;
    const email = formData.get('email') as string;
    const course = formData.get('course') as string;
    const message = formData.get('message') as string;

    try {
      const data = new URLSearchParams(formData as unknown as URLSearchParams);
      const res = await fetch('https://readdy.ai/api/form/d7742ciqo7glkto3e19g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      await supabase.from('consultations').insert([{
        name, phone, email: email || null, course, message: message || null, status: '대기중',
      }]);
      if (res.ok) { setSubmitStatus('success'); form.reset(); }
      else { setSubmitStatus('success'); form.reset(); }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative h-72 md:h-96 lg:h-[480px] flex items-center justify-center overflow-hidden">
          <img src="https://public.readdy.ai/ai/img_res/1289e6f1b13b328baea36676e4029e17.jpg" alt="수강생 모집" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 text-center px-5">
            <div className="page-hero-badge flex items-center justify-center gap-2 mb-3">
              <span className="w-6 h-px bg-[#C9A84C]" />
              <span className="text-[#C9A84C] text-xs font-bold tracking-widest uppercase">Academy</span>
              <span className="w-6 h-px bg-[#C9A84C]" />
            </div>
            <h1 className="page-hero-title text-3xl md:text-4xl lg:text-5xl font-black text-white">수강생 모집</h1>
          </div>
        </section>

        <div className="bg-[#F4F6FA] border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-5 md:px-6 py-3 flex items-center gap-2 text-xs text-gray-500">
            <i className="ri-home-line" /><span>홈</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-[#1B3A6B] font-semibold">수강생 모집</span>
          </div>
        </div>

        {/* 전체 과정 개요 배너 */}
        <section ref={sBanner.ref as React.RefObject<HTMLElement>} className="py-8 md:py-10 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className={`bg-[#F4F6FA] rounded-xl px-5 md:px-8 py-5 md:py-6 border border-gray-100 anim-fade-up ${sBanner.v ? 'is-visible' : ''}`}>
              <div className="flex flex-col md:flex-row items-center gap-5 md:gap-6">
                <div className="flex-shrink-0 text-center">
                  <p className="text-xs font-bold text-[#C9A84C] tracking-widest uppercase mb-1">전체 과정 구성</p>
                  <p className="text-lg md:text-xl font-black text-[#1B3A6B]">주 2회 · 2시간 · 12주</p>
                  <p className="text-gray-500 text-sm mt-0.5">총 48시간 완성 과정</p>
                </div>
                <div className="hidden md:block w-px h-12 bg-gray-200 flex-shrink-0" />
                <div className="flex-1 flex flex-col sm:flex-row items-center gap-3 w-full">
                  {[
                    { level: 'Level 1', name: '기초반', weeks: '1~4주', hours: '16시간', color: 'bg-[#1B3A6B]/10 text-[#1B3A6B]' },
                    { level: 'Level 2', name: '심화반', weeks: '5~8주', hours: '16시간', color: 'bg-[#1B3A6B]/20 text-[#1B3A6B]' },
                    { level: 'Level 3', name: '마스터반', weeks: '9~12주', hours: '16시간', color: 'bg-[#1B3A6B] text-white' },
                  ].map((step, i) => (
                    <div key={step.level} className="flex items-center gap-3 flex-1 w-full">
                      <div className={`flex-1 rounded-lg px-4 py-3 text-center ${step.color}`}>
                        <p className="text-xs font-bold opacity-70 mb-0.5">{step.level}</p>
                        <p className="font-black text-sm">{step.name}</p>
                        <p className="text-xs opacity-60 mt-0.5">{step.weeks} · {step.hours}</p>
                      </div>
                      {i < 2 && <i className="ri-arrow-right-line text-gray-300 flex-shrink-0 hidden sm:block" />}
                    </div>
                  ))}
                </div>
                <div className="hidden md:block w-px h-12 bg-gray-200 flex-shrink-0" />
                <div className="flex-shrink-0 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#C9A84C]/15 mx-auto mb-1">
                    <i className="ri-award-line text-[#C9A84C] text-xl" />
                  </div>
                  <p className="text-xs font-bold text-[#C9A84C]">임시자격증</p>
                  <p className="text-xs text-gray-400">8월 발급 예정</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 교육과정 */}
        <section ref={sCurriculum.ref as React.RefObject<HTMLElement>} className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className={`text-center mb-8 md:mb-12 anim-fade-up ${sCurriculum.v ? 'is-visible' : ''}`}>
              <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-4">교육과정</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B] mb-4">전문 교육 커리큘럼</h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">실무 중심의 체계적인 커리큘럼으로 현장에서 바로 활용 가능한 기술을 습득합니다</p>
            </div>
            <div className={`flex flex-wrap justify-center gap-2 mb-8 md:mb-10 anim-fade-up ${sCurriculum.v ? 'is-visible' : ''}`} style={{ transitionDelay: sCurriculum.v ? '150ms' : '0ms' }}>
              {curriculumList.map((c, i) => (
                <button key={c.id} onClick={() => setActiveTab(i)}
                  className={`whitespace-nowrap px-4 md:px-5 py-2 md:py-2.5 rounded-md text-sm font-semibold transition-all cursor-pointer flex items-center gap-2 ${activeTab === i ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F6FA] text-gray-600 hover:bg-[#1B3A6B]/10 hover:text-[#1B3A6B]'}`}>
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${activeTab === i ? 'bg-white/20 text-white' : 'bg-[#1B3A6B]/10 text-[#1B3A6B]'}`}>{c.level}</span>
                  {c.title}
                </button>
              ))}
            </div>
            {curriculumList[activeTab] && (
              <div key={`tab-${activeTab}`} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center bg-[#F4F6FA] rounded-xl p-5 md:p-8 border border-gray-100 anim-fade-up ${sCurriculum.v ? 'is-visible' : ''}`} style={{ transitionDelay: sCurriculum.v ? '250ms' : '0ms' }}>
                <div key={`img-${activeTab}`} className="rounded-xl overflow-hidden h-56 md:h-64 lg:h-80 bg-[#E8ECF4]">
                  <img src={curriculumList[activeTab].image} alt={curriculumList[activeTab].title} className="w-full h-full object-cover object-top" loading="eager" />
                </div>
                <div>
                  <span className="inline-block bg-[#C9A84C] text-white text-xs font-bold px-3 py-1 rounded-sm mb-4">{curriculumList[activeTab].badge}</span>
                  <p className="text-[#1B3A6B]/50 text-xs font-bold tracking-widest uppercase mb-1">{curriculumList[activeTab].level}</p>
                  <h3 className="text-xl md:text-2xl font-black text-[#1B3A6B] mb-1">{curriculumList[activeTab].title}</h3>
                  <p className="text-[#C9A84C] text-sm font-semibold mb-1">{curriculumList[activeTab].subtitle}</p>
                  <p className="text-gray-500 text-sm mb-2">{curriculumList[activeTab].duration}</p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{curriculumList[activeTab].desc}</p>
                  <div className="mb-5">
                    <p className="font-bold text-[#1B3A6B] text-sm mb-3">커리큘럼</p>
                    <ul className="space-y-2">
                      {curriculumList[activeTab].curriculum.map((step, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-sm text-gray-600">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#1B3A6B] text-white text-xs font-bold flex-shrink-0">{idx + 1}</span>
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mb-6 md:mb-7">
                    <p className="font-bold text-[#1B3A6B] text-sm mb-3">수강 혜택</p>
                    <ul className="space-y-2">
                      {curriculumList[activeTab].benefits.map((b) => (
                        <li key={b} className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-check-line text-[#C9A84C]" />{b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <a href="#consult" className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-[#1B3A6B] text-white font-bold hover:bg-[#15305a] transition-colors cursor-pointer w-full sm:w-auto">
                    이 과정 상담 신청 <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* 강사진 */}
        <section ref={sInstructor.ref as React.RefObject<HTMLElement>} className="py-14 md:py-20 bg-[#F4F6FA]">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className={`text-center mb-10 md:mb-14 anim-fade-up ${sInstructor.v ? 'is-visible' : ''}`}>
              <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-4">강사진</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B]">실무 전문 마스터 강사진</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {instructors.map((inst, i) => (
                <div key={inst.id} className={`bg-white rounded-xl overflow-hidden border border-gray-100 card-lift anim-scale-up ${sInstructor.v ? 'is-visible' : ''}`} style={{ transitionDelay: sInstructor.v ? `${i * 120}ms` : '0ms' }}>
                  <div className="h-52 md:h-56 overflow-hidden">
                    <img src={inst.image} alt={inst.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-5 md:p-6">
                    <p className="text-xs text-[#C9A84C] font-semibold tracking-wide mb-1">{inst.specialty}</p>
                    <h3 className="text-lg md:text-xl font-black text-[#1B3A6B] mb-1">{inst.name}</h3>
                    <p className="text-xs text-gray-400 mb-4">{inst.career}</p>
                    <p className="text-gray-500 text-sm leading-relaxed italic">&ldquo;{inst.quote}&rdquo;</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 수강생 혜택 */}
        <section ref={sBenefits.ref as React.RefObject<HTMLElement>} className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className={`text-center mb-10 md:mb-14 anim-fade-up ${sBenefits.v ? 'is-visible' : ''}`}>
              <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-4">수강생 혜택</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B]">중구지부만의 특별한 혜택</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {benefits.map((b, i) => (
                <div key={b.title} className={`bg-[#F4F6FA] rounded-xl p-5 md:p-7 border border-gray-100 hover:border-[#1B3A6B]/20 card-lift anim-scale-up ${sBenefits.v ? 'is-visible' : ''}`} style={{ transitionDelay: sBenefits.v ? `${i * 80}ms` : '0ms' }}>
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#1B3A6B]/10 text-[#1B3A6B] mb-4 md:mb-5">
                    <i className={`${b.icon} text-lg`} />
                  </div>
                  <h3 className="text-base font-bold text-[#1B3A6B] mb-2">{b.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 수강료 안내 */}
        <section ref={sFee.ref as React.RefObject<HTMLElement>} className="py-8 md:py-10 bg-[#1B3A6B]">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 items-center anim-fade-up ${sFee.v ? 'is-visible' : ''}`}>
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                {[
                  { icon: 'ri-money-dollar-circle-line', label: '전 과정 통합 수강료', value: '300만원', sub: 'Level 1~3 전체 포함' },
                  { icon: 'ri-calendar-check-line', label: '교육 일정', value: '주 2회 · 2시간', sub: '총 12주 · 48시간' },
                ].map((item, i) => (
                  <div key={item.label} className={`bg-white/10 border border-white/15 rounded-xl px-5 md:px-6 py-4 md:py-5 text-center anim-scale-up ${sFee.v ? 'is-visible' : ''}`} style={{ transitionDelay: sFee.v ? `${i * 100}ms` : '0ms' }}>
                    <i className={`${item.icon} text-[#C9A84C] text-2xl mb-2 block`} />
                    <p className="text-white/60 text-xs mb-1">{item.label}</p>
                    <p className="text-white font-black text-xl md:text-2xl">{item.value}</p>
                    <p className="text-white/40 text-xs mt-1">{item.sub}</p>
                  </div>
                ))}
                <div className={`bg-[#C9A84C]/15 border border-[#C9A84C]/30 rounded-xl px-5 md:px-6 py-4 md:py-5 text-center anim-scale-up ${sFee.v ? 'is-visible' : ''}`} style={{ transitionDelay: sFee.v ? '200ms' : '0ms' }}>
                  <i className="ri-award-line text-[#C9A84C] text-2xl mb-2 block" />
                  <p className="text-white/60 text-xs mb-1">임시자격증 발급</p>
                  <p className="text-[#C9A84C] font-black text-base md:text-lg">2026년 8월~</p>
                  <p className="text-white/40 text-xs mt-1">교육과정 수료자 대상</p>
                </div>
              </div>
              <div className={`bg-white/5 border border-white/10 rounded-xl p-4 md:p-5 anim-slide-right ${sFee.v ? 'is-visible' : ''}`} style={{ transitionDelay: sFee.v ? '300ms' : '0ms' }}>
                <p className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-[#C9A84C]" /> 안내사항
                </p>
                <ul className="space-y-2">
                  {['분할 납부 상담 가능', '수료 후 본회 자격증 시험 응시 자격 부여', '2026년 8월부터 임시자격증 발급 예정', '위생교육 이수증 별도 발급'].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-white/60">
                      <i className="ri-check-line text-[#C9A84C] flex-shrink-0 mt-0.5" />{item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 상담 신청 폼 */}
        <section ref={sConsult.ref as React.RefObject<HTMLElement>} id="consult" className="py-14 md:py-20 bg-[#F4F6FA]">
          <div className="max-w-2xl mx-auto px-5 md:px-6">
            <div className={`text-center mb-8 md:mb-10 anim-fade-up ${sConsult.v ? 'is-visible' : ''}`}>
              <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-4">상담 신청</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B] mb-3">무료 상담 신청</h2>
              <p className="text-gray-500 text-sm">전문 상담사가 24시간 내 연락드립니다</p>
            </div>

            {submitStatus === 'success' ? (
              <div className={`bg-white border border-[#1B3A6B]/20 rounded-xl p-8 md:p-10 text-center anim-scale-up ${sConsult.v ? 'is-visible' : ''}`} style={{ transitionDelay: sConsult.v ? '150ms' : '0ms' }}>
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1B3A6B]/10 mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-3xl text-[#1B3A6B]" />
                </div>
                <p className="text-[#1B3A6B] font-black text-xl mb-2">신청이 완료되었습니다!</p>
                <p className="text-gray-500 text-sm">담당자가 24시간 내에 연락드리겠습니다.</p>
                <button onClick={() => setSubmitStatus('idle')} className="whitespace-nowrap mt-6 px-6 py-3 rounded-md bg-[#1B3A6B] text-white font-semibold cursor-pointer hover:bg-[#15305a] transition-colors w-full sm:w-auto">
                  다시 신청하기
                </button>
              </div>
            ) : (
              <form data-readdy-form id="consult-form" onSubmit={handleSubmit} className={`bg-white rounded-xl p-6 md:p-8 border border-gray-100 space-y-5 anim-scale-up ${sConsult.v ? 'is-visible' : ''}`} style={{ transitionDelay: sConsult.v ? '150ms' : '0ms' }}>
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2">이름 <span className="text-red-500">*</span></label>
                  <input name="name" type="text" required placeholder="홍길동" className="w-full border border-gray-200 text-gray-800 placeholder-gray-400 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B]" />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2">연락처 <span className="text-red-500">*</span></label>
                  <input name="phone" type="tel" required placeholder="010-0000-0000" className="w-full border border-gray-200 text-gray-800 placeholder-gray-400 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B]" />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2">이메일</label>
                  <input name="email" type="email" placeholder="example@email.com" className="w-full border border-gray-200 text-gray-800 placeholder-gray-400 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B]" />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2">희망 과정 <span className="text-red-500">*</span></label>
                  <select name="course" required className="w-full border border-gray-200 text-gray-800 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B] cursor-pointer bg-white">
                    <option value="">과정을 선택해주세요</option>
                    <option value="반영구 문신 마스터 과정">반영구 문신 마스터 과정</option>
                    <option value="두피 반영구 문신 전문가 과정">두피 반영구 문신 전문가 과정</option>
                    <option value="입술·아이라인 반영구 문신 과정">입술·아이라인 반영구 문신 과정</option>
                    <option value="기타 문의">기타 문의</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2">문의 내용</label>
                  <textarea name="message" placeholder="궁금하신 점을 자유롭게 작성해주세요. (최대 500자)" maxLength={500} rows={4}
                    className="w-full border border-gray-200 text-gray-800 placeholder-gray-400 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B] resize-none" />
                </div>
                {submitStatus === 'error' && <p className="text-red-500 text-sm text-center">전송 중 오류가 발생했습니다. 다시 시도해주세요.</p>}
                <button type="submit" className="whitespace-nowrap w-full py-4 rounded-md bg-[#1B3A6B] text-white font-bold text-base hover:bg-[#15305a] transition-colors cursor-pointer">
                  무료 상담 신청하기
                </button>
                <p className="text-gray-400 text-xs text-center">개인정보는 상담 목적으로만 사용되며 안전하게 보호됩니다</p>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
